import React from 'react'
import { twMerge } from 'tailwind-merge'

import { Box } from '../box'
import { DateTimePicker } from './DateTimePicker'

export interface DateTimeRange {
    start?: Date | null
    end?: Date | null
}

export interface DateTimeRangePickerProps {
    value: DateTimeRange
    onChange: (range: DateTimeRange) => void
    startPlaceholder?: string
    endPlaceholder?: string
    startLabel?: string
    endLabel?: string
    required?: boolean
    disabled?: boolean
    linkEndToStart?: boolean
    clearableEnd?: boolean
    iconTooltip?: string
    className?: string
}

const startOfDay = (date: Date): Date =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate())

const addHour = (date: Date): Date => {
    const next = new Date(date)
    next.setHours(next.getHours() + 1)
    return next
}

const subtractHour = (date: Date): Date => {
    const next = new Date(date)
    next.setHours(next.getHours() - 1)
    return next
}

export const DateTimeRangePicker: React.FC<DateTimeRangePickerProps> = ({
    value,
    onChange,
    startPlaceholder,
    endPlaceholder,
    startLabel,
    endLabel,
    required,
    disabled = false,
    linkEndToStart = false,
    clearableEnd = false,
    iconTooltip,
    className,
}) => {
    const changeStart = (start: Date) => {
        if (linkEndToStart) {
            onChange({ start, end: addHour(start) })
            return
        }
        onChange({ start, end: value.end })
    }

    const changeEnd = (end: Date) => {
        if (!value.start) {
            onChange({ start: subtractHour(end), end })
            return
        }
        onChange({ start: value.start, end })
    }

    const clearEnd = () => onChange({ start: value.start, end: null })

    const endDateDisabled = (date: Date): boolean =>
        value.start ? startOfDay(date).getTime() < startOfDay(value.start).getTime() : false

    return (
        <Box className={twMerge('gap-2', className)}>
            <DateTimePicker
                value={value.start}
                onChange={changeStart}
                label={startLabel}
                placeholder={startPlaceholder}
                required={required}
                disabled={disabled}
                tooltip={iconTooltip}
                className='w-full'
            />
            <DateTimePicker
                value={value.end}
                onChange={changeEnd}
                onClear={clearableEnd ? clearEnd : undefined}
                label={endLabel}
                placeholder={endPlaceholder}
                required={required}
                disabled={disabled}
                isDateDisabled={endDateDisabled}
                tooltip={iconTooltip}
                className='w-full'
            />
        </Box>
    )
}

DateTimeRangePicker.displayName = 'DateTimeRangePicker'
